import { CATALYST_COLORS_WITH_HEXCODES } from "@certa/catalyst/constants/styles";
import type { DataObjectFieldRAGRuleRaw } from "@certa/types/src/dataObjects.types";
import type { IntlShape } from "react-intl";

const RAG_BACKGROUND_COLORS = {
  red: CATALYST_COLORS_WITH_HEXCODES.RED_200,
  amber: CATALYST_COLORS_WITH_HEXCODES.ORANGE_200,
  green: CATALYST_COLORS_WITH_HEXCODES.GREEN_200
} as const;

const RAG_COLORS = {
  red: CATALYST_COLORS_WITH_HEXCODES.RED_400,
  amber: CATALYST_COLORS_WITH_HEXCODES.ORANGE_400,
  green: CATALYST_COLORS_WITH_HEXCODES.GREEN_400
} as const;

export const getRAGIndicator = (
  rag: DataObjectFieldRAGRuleRaw,
  value: string | number,
  intl: IntlShape
) => {
  // Check each RAG color rule
  for (const [color, rule] of Object.entries(rag)) {
    if (!rule) continue;

    const {
      operator,
      value: compareValue,
      values: compareValues,
      min,
      max
    } = rule;

    const isRuleConditionMatched = () => {
      switch (operator) {
        case "eq":
          return value === compareValue;
        case "not_eq":
          return value !== compareValue;
        case "gt":
          return value > compareValue;
        case "gte":
          return value >= compareValue;
        case "lt":
          return value < compareValue;
        case "lte":
          return value <= compareValue;
        case "in":
          return compareValues?.some(v => v === value);
        case "not_in":
          return compareValues?.every(v => v !== value);
        case "between":
          return (
            typeof min === "number" &&
            typeof max === "number" &&
            typeof value === "number" &&
            value >= min &&
            value <= max
          );
        default:
          return false;
      }
    };

    if (isRuleConditionMatched()) {
      let defaultLabel = intl.formatMessage({
        id: "dataObject.rag.mediumRisk",
        defaultMessage: "Medium Risk"
      });
      if (color === "red")
        defaultLabel = intl.formatMessage({
          id: "dataObject.rag.highRisk",
          defaultMessage: "High Risk"
        });
      if (color === "green")
        defaultLabel = intl.formatMessage({
          id: "dataObject.rag.lowRisk",
          defaultMessage: "Low Risk"
        });

      return {
        colorCode: color as keyof typeof RAG_COLORS,
        color: RAG_COLORS[color as keyof typeof RAG_COLORS],
        backgroundColor:
          RAG_BACKGROUND_COLORS[color as keyof typeof RAG_BACKGROUND_COLORS],
        label: rule.label || defaultLabel,
        description: rule.description
      };
    }
  }

  return null;
};
